import { adjustAsInUTC, toLocalMidnightDate } from '../date/paramsUtc';
import { toDateString } from '../dates/Dates';

//my idea of Event is something that
//is associated with a start time, and a game
//and is Contributable
export type QueryEvents<
  TEvent extends { id: string; date: Date },
  TQuery,
  TDate = Date,
> = (
  gameIds: string[] | 'ALL',
  startDate: TDate,
  endDate: TDate,
  queryModifier?: (query: TQuery) => TQuery,
) => Promise<TEvent[]>;

export class EventsFetcher<TEvent extends { id: string; date: Date }, TQuery> {
  constructor(
    private queryEvents: QueryEvents<TEvent, TQuery, Date>,
    private readonly utcOffsetHours?: number,
  ) {}

  public async fetch(
    ...[gameIds, startDate, endDate, queryModifier]: Parameters<
      QueryEvents<TEvent, TQuery, string>
    >
  ) {
    const startDateMidnight = this.utcOffsetHours
      ? new Date(startDate)
      : toLocalMidnightDate(startDate);
    const endDateMidnight = this.utcOffsetHours
      ? new Date(endDate)
      : toLocalMidnightDate(endDate);
    const [startDateEarliest, endDateLatest] = this.rangeToTimestamp(
      startDateMidnight,
      endDateMidnight,
    );
    const events = await this.queryEvents(
      gameIds,
      startDateEarliest,
      endDateLatest,
      queryModifier,
    );

    return EventsFetcher.groupByDate(events);
  }

  // eslint-disable-next-line no-shadow
  public static groupByDate<TEvent extends { id: string; date: Date }>(
    events: TEvent[],
  ): Record<string, TEvent[]> {
    return events.reduce((prev, curr) => {
      const dateEvent = toDateString(curr.date);
      (prev[String(dateEvent)] ??= []).push(curr);
      return prev;
    }, {} as Record<string, TEvent[]>);
  }

  private rangeToTimestamp(startDate: Date, endDate: Date): [Date, Date] {
    const startDateEarliest = new Date(startDate.setHours(0, 0, 0, 0));
    const endDateLatest = new Date(endDate.setHours(23, 59, 59, 999));
    if (this.utcOffsetHours) {
      return [
        adjustAsInUTC(startDateEarliest, this.utcOffsetHours),
        adjustAsInUTC(endDateLatest, this.utcOffsetHours),
      ];
    }
    return [startDateEarliest, endDateLatest];
  }
}
